import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3, TrendingUp, Award, AlertTriangle, MousePointerClick,
  Eye, RefreshCw, Send, Mail, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Activity,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  AreaChart, Area,
} from 'recharts';
import { dashboardApi } from '../services/emailApi';
import './EmailAnalytics.css';

/* ── Palette ── */
const COLORS = {
  primary: '#6366f1', info: '#3b82f6', success: '#10b981',
  warning: '#f59e0b', danger: '#ef4444', purple: '#8b5cf6',
  pink: '#ec4899', teal: '#14b8a6',
};
const PIE_COLORS = [COLORS.primary, COLORS.info, COLORS.success, COLORS.warning, COLORS.danger, COLORS.purple, COLORS.pink];

/* ── Tiny pagination hook ── */
const usePagination = (data, perPage = 5) => {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [data]);
  const totalPages = Math.max(1, Math.ceil((data?.length || 0) / perPage));
  const safeP = Math.min(page, totalPages);
  const sliced = (data || []).slice((safeP - 1) * perPage, safeP * perPage);
  return { page: safeP, totalPages, sliced, setPage, total: data?.length || 0 };
};

/* ── Pagination bar component ── */
const PaginationBar = ({ page, totalPages, setPage, total, label = 'items' }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="ema-pagination">
      <span className="ema-pagination-info">{total} {label}</span>
      <div className="ema-pagination-controls">
        <button className="ema-pg-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={14} /></button>
        <span className="ema-pg-text">{page} / {totalPages}</span>
        <button className="ema-pg-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={14} /></button>
      </div>
    </div>
  );
};

/* ── Custom recharts tooltip ── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ema-chart-tooltip">
      {label && <p className="ema-tooltip-label">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="ema-tooltip-row">
          <span className="ema-tooltip-dot" style={{ background: p.color }} />
          {p.name}: <strong>{(p.value ?? 0).toLocaleString()}</strong>
        </p>
      ))}
    </div>
  );
};

/* ── Collapsible section ── */
const Collapsible = ({ title, count, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ema-collapsible">
      <button className="ema-collapsible-toggle" onClick={() => setOpen(!open)}>
        <span>{title}{count != null && <span className="ema-badge">{count}</span>}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="ema-collapsible-body">{children}</div>}
    </div>
  );
};

/* ── Skeleton helper ── */
const Skeleton = ({ rows = 3, style }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16, ...style }}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="em-skl" style={{ height: 18, borderRadius: 6, animationDelay: `${i * 80}ms` }} />
    ))}
  </div>
);

/* ═══════════════════ MAIN COMPONENT ═══════════════════ */
const EmailAnalytics = () => {
  const [period, setPeriod] = useState('30d');
  const [sendingStats, setSendingStats] = useState([]);
  const [sendingStatsSummary, setSendingStatsSummary] = useState(null);
  const [topCampaigns, setTopCampaigns] = useState([]);
  const [bounceStats, setBounceStats] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [templatePerf, setTemplatePerf] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAll = useCallback(async () => {
    try {
      const groupBy = period === '24h' ? 'hour' : period === '90d' ? 'week' : 'day';
      const [statsRes, topRes, bounceRes, engRes, tplRes] = await Promise.allSettled([
        dashboardApi.sendingStats({ period, groupBy }),
        dashboardApi.topCampaigns({ period, limit: 5 }),
        dashboardApi.bounceStats({ period }),
        dashboardApi.engagement(),
        dashboardApi.templatePerformance({ period, limit: 5 }),
      ]);

      if (statsRes.status === 'fulfilled' && statsRes.value?.data?.success) {
        const resData = statsRes.value.data.data || {};
        const stats = resData.stats;
        // stats can be a summary object or an array of time-series data
        if (stats && !Array.isArray(stats)) {
          // Summary object: { total, sent, delivered, opened, clicked, bounced, failed, complained }
          setSendingStatsSummary({
            total: Number(stats.total || 0),
            sent: Number(stats.sent || 0),
            delivered: Number(stats.delivered || 0),
            opened: Number(stats.opened || 0),
            clicked: Number(stats.clicked || 0),
            bounced: Number(stats.bounced || 0),
            failed: Number(stats.failed || 0),
            complained: Number(stats.complained || 0),
          });
          setSendingStats([]);
        } else {
          setSendingStats(Array.isArray(stats) ? stats : []);
        }
      }
      if (topRes.status === 'fulfilled' && topRes.value?.data?.success) {
        const topData = topRes.value.data.data;
        const campaigns = topData?.campaigns || (Array.isArray(topData) ? topData : []);
        setTopCampaigns(Array.isArray(campaigns) ? campaigns : []);
      }
      if (bounceRes.status === 'fulfilled' && bounceRes.value?.data?.success) {
        const bd = bounceRes.value.data.data || {};
        // Compute aggregate bounce numbers from campaignBounces array
        const cb = Array.isArray(bd.campaignBounces) ? bd.campaignBounces : [];
        const totalBounced = cb.reduce((s, c) => s + Number(c.bounced_count || 0), 0);
        const totalFailed = cb.reduce((s, c) => s + Number(c.failed_count || 0), 0);
        const totalSent = cb.reduce((s, c) => s + Number(c.sent_count || 0), 0);
        // Extract hard/soft from bouncesByType if available
        const byType = Array.isArray(bd.bouncesByType) ? bd.bouncesByType : [];
        const hard = byType.find(b => b.type === 'hard' || b.bounce_type === 'hard');
        const soft = byType.find(b => b.type === 'soft' || b.bounce_type === 'soft');
        setBounceStats({
          ...bd,
          hardBounces: hard ? Number(hard.count || hard.total || 0) : 0,
          softBounces: soft ? Number(soft.count || soft.total || 0) : 0,
          totalBounces: totalBounced + totalFailed,
          bounceRate: totalSent > 0 ? ((totalBounced + totalFailed) / totalSent * 100) : 0,
          recentBounces: bd.recentBounces || [],
          campaignBounces: cb,
        });
      }
      if (engRes.status === 'fulfilled' && engRes.value?.data?.success) {
        const ed = engRes.value.data.data || {};
        // Compute aggregate engagement rates from campaignEngagement array
        const ce = Array.isArray(ed.campaignEngagement) ? ed.campaignEngagement : [];
        const totSent = ce.reduce((s, c) => s + Number(c.sent_count || 0), 0);
        const totDelivered = ce.reduce((s, c) => s + Number(c.delivered_count || 0), 0);
        const totOpened = ce.reduce((s, c) => s + Number(c.opened_count || 0), 0);
        const totClicked = ce.reduce((s, c) => s + Number(c.clicked_count || 0), 0);
        const totBounced = ce.reduce((s, c) => s + Number(c.bounced_count || 0), 0);
        setEngagement({
          ...ed,
          openRate: totDelivered > 0 ? (totOpened / totDelivered * 100) : (ed.openRate || ed.open_rate || 0),
          clickRate: totDelivered > 0 ? (totClicked / totDelivered * 100) : (ed.clickRate || ed.click_rate || 0),
          bounceRate: totSent > 0 ? (totBounced / totSent * 100) : (ed.bounceRate || ed.bounce_rate || 0),
          unsubscribeRate: ed.unsubscribeRate || ed.unsubscribe_rate || 0,
        });
      }
      if (tplRes.status === 'fulfilled' && tplRes.value?.data?.success) {
        const tplData = tplRes.value.data.data;
        const tpls = tplData?.templates || (Array.isArray(tplData) ? tplData : []);
        setTemplatePerf(Array.isArray(tpls) ? tpls : []);
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  const handleRefresh = () => { setRefreshing(true); fetchAll(); };

  /* ── Formatters ── */
  const fmtNum = (n) => (n ?? 0).toLocaleString();
  const fmtPct = (n) => `${(parseFloat(n) || 0).toFixed(1)}%`;

  /* ── Derived chart data ── */
  const donutData = useMemo(() => {
    if (!sendingStatsSummary) return [];
    const s = sendingStatsSummary;
    return [
      { name: 'Sent', value: s.sent },
      { name: 'Delivered', value: s.delivered },
      { name: 'Opened', value: s.opened },
      { name: 'Clicked', value: s.clicked },
      { name: 'Bounced', value: s.bounced },
      { name: 'Failed', value: s.failed },
    ].filter(d => d.value > 0);
  }, [sendingStatsSummary]);

  const campaignBarData = useMemo(() => {
    return topCampaigns.map(c => ({
      name: (c.name || '').length > 18 ? c.name.substring(0, 18) + '…' : c.name,
      Sent: Number(c.sent_count || c.total_sent || 0),
      Failed: Number(c.failed_count || 0),
      Recipients: Number(c.total_recipients || 0),
    }));
  }, [topCampaigns]);

  const bounceBarData = useMemo(() => {
    if (!bounceStats?.campaignBounces) return [];
    return bounceStats.campaignBounces
      .filter(c => Number(c.failed_count || 0) > 0 || Number(c.bounced_count || 0) > 0)
      .slice(0, 10)
      .map(c => ({
        name: (c.campaign_name || '').length > 16 ? c.campaign_name.substring(0, 16) + '…' : c.campaign_name,
        Failed: Number(c.failed_count || 0),
        Bounced: Number(c.bounced_count || 0),
        Sent: Number(c.sent_count || 0),
      }));
  }, [bounceStats]);

  const templateBarData = useMemo(() => {
    return templatePerf.map(t => ({
      name: (t.name || '').length > 16 ? t.name.substring(0, 16) + '…' : t.name,
      Campaigns: Number(t.campaigns_count || 0),
      Sent: Number(t.total_sent || 0),
      Failed: Number(t.total_failed || 0),
    }));
  }, [templatePerf]);

  const engagementAreaData = useMemo(() => {
    if (!engagement?.campaignEngagement) return [];
    return [...engagement.campaignEngagement].reverse().map(c => ({
      name: (c.name || '').length > 14 ? c.name.substring(0, 14) + '…' : c.name,
      Sent: Number(c.sent_count || 0),
      Delivered: Number(c.delivered_count || 0),
      Opened: Number(c.opened_count || 0),
      Clicked: Number(c.clicked_count || 0),
    }));
  }, [engagement]);

  /* ── Paginated data ── */
  const bounceCampaignPg = usePagination(bounceStats?.campaignBounces, 5);
  const recentBouncePg = usePagination(bounceStats?.recentBounces, 5);
  const engagementPg = usePagination(engagement?.campaignEngagement, 5);

  /* ═══════════ RENDER ═══════════ */
  return (
    <div className="ema-page">
      {/* ── Header ── */}
      <div className="ema-header">
        <div>
          <h1 className="ema-title">
            <Activity size={22} className="ema-title-icon" />
            Email Analytics
          </h1>
          <p className="ema-subtitle">Deep dive into your email marketing performance</p>
        </div>
        <div className="ema-header-actions">
          <div className="ema-period-tabs">
            {['24h', '7d', '30d', '90d'].map(p => (
              <button key={p} className={`ema-period-btn ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>{p}</button>
            ))}
          </div>
          <button className={`ema-refresh-btn ${refreshing ? 'spinning' : ''}`} onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={15} className={refreshing ? 'spin-icon' : ''} />
          </button>
        </div>
      </div>

      {/* ── Tab Navigation ── */}
      <div className="ema-tabs">
        {[
          { key: 'overview', label: 'Overview', icon: <BarChart3 size={15} /> },
          { key: 'campaigns', label: 'Campaigns', icon: <Award size={15} /> },
          { key: 'bounces', label: 'Bounces', icon: <AlertTriangle size={15} /> },
          { key: 'templates', label: 'Templates', icon: <TrendingUp size={15} /> },
        ].map(t => (
          <button key={t.key} className={`ema-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* ═══ OVERVIEW TAB ═══ */}
      {activeTab === 'overview' && (
        <>
          {/* Engagement Rate KPIs */}
          {loading ? (
            <div className="ema-kpi-row">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="ema-kpi-card">
                  <div className="em-skl" style={{ width: 44, height: 44, borderRadius: 12 }} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div className="em-skl" style={{ width: '60%', height: 24 }} />
                    <div className="em-skl" style={{ width: '40%', height: 13 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : engagement && (
            <div className="ema-kpi-row">
              {[
                { label: 'Open Rate', value: fmtPct(engagement.openRate), icon: <Eye size={20} />, color: 'primary' },
                { label: 'Click Rate', value: fmtPct(engagement.clickRate), icon: <MousePointerClick size={20} />, color: 'info' },
                { label: 'Bounce Rate', value: fmtPct(engagement.bounceRate), icon: <AlertTriangle size={20} />, color: 'danger' },
                { label: 'Unsubscribe Rate', value: fmtPct(engagement.unsubscribeRate), icon: <Activity size={20} />, color: 'warning' },
              ].map((k, i) => (
                <div key={i} className={`ema-kpi-card ema-kpi-${k.color}`}>
                  <div className={`ema-kpi-icon ema-kpi-icon-${k.color}`}>{k.icon}</div>
                  <div>
                    <h3 className="ema-kpi-value">{k.value}</h3>
                    <p className="ema-kpi-label">{k.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sending Volume KPIs */}
          {!loading && sendingStatsSummary && (
            <div className="ema-kpi-row ema-kpi-row-sm">
              {[
                { label: 'Total Emails', value: fmtNum(sendingStatsSummary.total), icon: <Mail size={18} />, color: 'primary' },
                { label: 'Sent', value: fmtNum(sendingStatsSummary.sent), icon: <Send size={18} />, color: 'info' },
                { label: 'Delivered', value: fmtNum(sendingStatsSummary.delivered), icon: <CheckCircle size={18} />, color: 'success' },
                { label: 'Opened', value: fmtNum(sendingStatsSummary.opened), icon: <Eye size={18} />, color: 'purple' },
                { label: 'Clicked', value: fmtNum(sendingStatsSummary.clicked), icon: <MousePointerClick size={18} />, color: 'info' },
                { label: 'Bounced', value: fmtNum(sendingStatsSummary.bounced), icon: <AlertTriangle size={18} />, color: 'warning' },
                { label: 'Failed', value: fmtNum(sendingStatsSummary.failed), icon: <XCircle size={18} />, color: 'danger' },
              ].map((k, i) => (
                <div key={i} className={`ema-kpi-card-sm ema-kpi-${k.color}`}>
                  <div className={`ema-kpi-icon-sm ema-kpi-icon-${k.color}`}>{k.icon}</div>
                  <div>
                    <h3 className="ema-kpi-value-sm">{k.value}</h3>
                    <p className="ema-kpi-label-sm">{k.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Charts Row */}
          <div className="ema-grid-2">
            {/* Donut – Email Breakdown */}
            <div className="ema-card">
              <div className="ema-card-head">
                <h3 className="ema-card-title"><Mail size={16} /> Email Breakdown</h3>
              </div>
              <div className="ema-card-body" style={{ minHeight: 260 }}>
                {loading ? <Skeleton rows={5} /> : donutData.length === 0 ? (
                  <div className="ema-empty"><BarChart3 size={28} /><p>No email data yet</p></div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" stroke="none">
                        {donutData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                      <Legend verticalAlign="bottom" iconType="circle" iconSize={8}
                        formatter={(v) => <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{v}</span>} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Bar – Top Campaigns */}
            <div className="ema-card">
              <div className="ema-card-head">
                <h3 className="ema-card-title"><Award size={16} /> Top Campaigns</h3>
              </div>
              <div className="ema-card-body" style={{ minHeight: 260 }}>
                {loading ? <Skeleton rows={5} /> : campaignBarData.length === 0 ? (
                  <div className="ema-empty"><Award size={28} /><p>No campaigns yet</p></div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={campaignBarData} barGap={2} barSize={16}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="Sent" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Failed" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* Engagement Area Chart – full width */}
          {engagementAreaData.length > 0 && !loading && (
            <div className="ema-card ema-card-full">
              <div className="ema-card-head">
                <h3 className="ema-card-title"><Activity size={16} /> Campaign Engagement Trend</h3>
              </div>
              <div className="ema-card-body">
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={engagementAreaData}>
                    <defs>
                      <linearGradient id="gradSent" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.15} /><stop offset="100%" stopColor={COLORS.primary} stopOpacity={0} /></linearGradient>
                      <linearGradient id="gradDel" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.success} stopOpacity={0.15} /><stop offset="100%" stopColor={COLORS.success} stopOpacity={0} /></linearGradient>
                      <linearGradient id="gradOpen" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLORS.info} stopOpacity={0.15} /><stop offset="100%" stopColor={COLORS.info} stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="Sent" stroke={COLORS.primary} fill="url(#gradSent)" strokeWidth={2} />
                    <Area type="monotone" dataKey="Delivered" stroke={COLORS.success} fill="url(#gradDel)" strokeWidth={2} />
                    <Area type="monotone" dataKey="Opened" stroke={COLORS.info} fill="url(#gradOpen)" strokeWidth={2} />
                    <Legend verticalAlign="top" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.75rem', paddingBottom: 8 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ CAMPAIGNS TAB ═══ */}
      {activeTab === 'campaigns' && (
        <>
          {/* Campaign Bar Chart */}
          {campaignBarData.length > 0 && !loading && (
            <div className="ema-card ema-card-full">
              <div className="ema-card-head">
                <h3 className="ema-card-title"><Award size={16} /> Top Campaigns Overview</h3>
              </div>
              <div className="ema-card-body">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={campaignBarData} barSize={22}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend verticalAlign="top" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.75rem', paddingBottom: 8 }} />
                    <Bar dataKey="Recipients" fill={COLORS.teal} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Sent" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Failed" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top Campaigns Detail Table */}
          <div className="ema-card">
            <div className="ema-card-head">
              <h3 className="ema-card-title"><Award size={16} /> Campaign Details</h3>
            </div>
            <div className="ema-card-body ema-no-pad">
              {loading ? <Skeleton rows={4} /> : topCampaigns.length === 0 ? (
                <div className="ema-empty"><Award size={28} /><p>No campaign data</p></div>
              ) : (
                <div className="ema-table-wrap">
                  <table className="ema-table">
                    <thead>
                      <tr><th>#</th><th>Campaign</th><th>Subject</th><th>Status</th><th>Recipients</th><th>Sent</th><th>Opens</th><th>Clicks</th><th>Delivery</th></tr>
                    </thead>
                    <tbody>
                      {topCampaigns.map((c, i) => (
                        <tr key={c.id || i}>
                          <td><span className="ema-rank">{i + 1}</span></td>
                          <td className="ema-cell-name">{c.name}</td>
                          <td className="ema-cell-sub">{c.subject || '—'}</td>
                          <td><span className={`ema-status-pill ema-st-${c.status}`}>{c.status}</span></td>
                          <td>{fmtNum(c.total_recipients)}</td>
                          <td>{fmtNum(c.sent_count || c.total_sent || 0)}</td>
                          <td>{fmtPct(c.open_rate)}</td>
                          <td>{fmtPct(c.click_rate)}</td>
                          <td>{c.delivery_rate != null ? fmtPct(c.delivery_rate) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Campaign Engagement Table */}
          {engagement?.campaignEngagement?.length > 0 && !loading && (
            <div className="ema-card">
              <div className="ema-card-head">
                <h3 className="ema-card-title"><Activity size={16} /> Campaign Engagement</h3>
              </div>
              <div className="ema-card-body ema-no-pad">
                <div className="ema-table-wrap">
                  <table className="ema-table">
                    <thead>
                      <tr><th>Campaign</th><th>Status</th><th>Sent</th><th>Delivered</th><th>Opened</th><th>Clicked</th><th>Open Rate</th><th>Click Rate</th></tr>
                    </thead>
                    <tbody>
                      {engagementPg.sliced.map((ce, i) => (
                        <tr key={ce.id || i}>
                          <td className="ema-cell-name">{ce.name}</td>
                          <td><span className={`ema-status-pill ema-st-${ce.status}`}>{ce.status}</span></td>
                          <td>{fmtNum(ce.sent_count)}</td>
                          <td>{fmtNum(ce.delivered_count)}</td>
                          <td>{fmtNum(ce.opened_count)}</td>
                          <td>{fmtNum(ce.clicked_count)}</td>
                          <td>{fmtPct(ce.open_rate)}</td>
                          <td>{fmtPct(ce.click_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <PaginationBar {...engagementPg} label="campaigns" />
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ BOUNCES TAB ═══ */}
      {activeTab === 'bounces' && (
        <>
          {/* Bounce Summary KPIs */}
          {!loading && bounceStats && (
            <div className="ema-kpi-row">
              {[
                { label: 'Hard Bounces', value: fmtNum(bounceStats.hardBounces), icon: <XCircle size={20} />, color: 'danger' },
                { label: 'Soft Bounces', value: fmtNum(bounceStats.softBounces), icon: <AlertTriangle size={20} />, color: 'warning' },
                { label: 'Total Bounces / Failures', value: fmtNum(bounceStats.totalBounces), icon: <Activity size={20} />, color: 'purple' },
                { label: 'Bounce Rate', value: fmtPct(bounceStats.bounceRate), icon: <TrendingUp size={20} />, color: 'info' },
              ].map((k, i) => (
                <div key={i} className={`ema-kpi-card ema-kpi-${k.color}`}>
                  <div className={`ema-kpi-icon ema-kpi-icon-${k.color}`}>{k.icon}</div>
                  <div>
                    <h3 className="ema-kpi-value">{k.value}</h3>
                    <p className="ema-kpi-label">{k.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading && <Skeleton rows={4} />}

          {/* Bounce Bar Chart */}
          {bounceBarData.length > 0 && !loading && (
            <div className="ema-card ema-card-full">
              <div className="ema-card-head">
                <h3 className="ema-card-title"><BarChart3 size={16} /> Bounces by Campaign</h3>
              </div>
              <div className="ema-card-body">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={bounceBarData} barSize={18}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} angle={-20} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend verticalAlign="top" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.75rem', paddingBottom: 8 }} />
                    <Bar dataKey="Failed" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Bounced" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Sent" fill={COLORS.success} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Campaign Bounces Table */}
          {bounceStats?.campaignBounces?.length > 0 && !loading && (
            <div className="ema-card">
              <div className="ema-card-head">
                <h3 className="ema-card-title"><AlertTriangle size={16} /> Campaign Bounce Details</h3>
              </div>
              <div className="ema-card-body ema-no-pad">
                <div className="ema-table-wrap">
                  <table className="ema-table">
                    <thead>
                      <tr><th>Campaign</th><th>Sent</th><th>Failed</th><th>Bounced</th><th>Rate</th></tr>
                    </thead>
                    <tbody>
                      {bounceCampaignPg.sliced.map((cb, i) => (
                        <tr key={i}>
                          <td className="ema-cell-name">{cb.campaign_name}</td>
                          <td>{fmtNum(cb.sent_count)}</td>
                          <td className="ema-text-danger">{fmtNum(cb.failed_count)}</td>
                          <td>{fmtNum(cb.bounced_count)}</td>
                          <td>{fmtPct(cb.bounce_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <PaginationBar {...bounceCampaignPg} label="campaigns" />
              </div>
            </div>
          )}

          {/* Recent Bounces */}
          {bounceStats?.recentBounces?.length > 0 && !loading && (
            <div className="ema-card">
              <Collapsible title="Recent Bounce Errors" count={bounceStats.recentBounces.length} defaultOpen={false}>
                <div className="ema-table-wrap">
                  <table className="ema-table ema-table-compact">
                    <thead>
                      <tr><th>Email</th><th>Campaign</th><th>Type</th><th>Error</th></tr>
                    </thead>
                    <tbody>
                      {recentBouncePg.sliced.map((rb, i) => (
                        <tr key={i}>
                          <td className="ema-cell-email">{rb.email_address}</td>
                          <td className="ema-cell-sub">{rb.campaign_name || '—'}</td>
                          <td>{rb.bounce_type || <span className="ema-muted">—</span>}</td>
                          <td className="ema-cell-error">{rb.error_message ? (rb.error_message.length > 80 ? rb.error_message.substring(0, 80) + '…' : rb.error_message) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <PaginationBar {...recentBouncePg} label="bounces" />
              </Collapsible>
            </div>
          )}
        </>
      )}

      {/* ═══ TEMPLATES TAB ═══ */}
      {activeTab === 'templates' && (
        <>
          {/* Template Bar Chart */}
          {templateBarData.length > 0 && !loading && (
            <div className="ema-card ema-card-full">
              <div className="ema-card-head">
                <h3 className="ema-card-title"><TrendingUp size={16} /> Template Usage</h3>
              </div>
              <div className="ema-card-body">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={templateBarData} barSize={20}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend verticalAlign="top" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.75rem', paddingBottom: 8 }} />
                    <Bar dataKey="Campaigns" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Sent" fill={COLORS.success} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Failed" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Template Table */}
          <div className="ema-card">
            <div className="ema-card-head">
              <h3 className="ema-card-title"><TrendingUp size={16} /> Template Performance</h3>
            </div>
            <div className="ema-card-body ema-no-pad">
              {loading ? <Skeleton rows={4} /> : templatePerf.length === 0 ? (
                <div className="ema-empty"><TrendingUp size={28} /><p>No templates yet</p></div>
              ) : (
                <div className="ema-table-wrap">
                  <table className="ema-table">
                    <thead>
                      <tr><th>Template</th><th>Category</th><th>Campaigns</th><th>Sent</th><th>Delivered</th><th>Open Rate</th><th>Click Rate</th><th>Failed</th></tr>
                    </thead>
                    <tbody>
                      {templatePerf.map((t, i) => (
                        <tr key={t.id || i}>
                          <td className="ema-cell-name">{t.name}</td>
                          <td><span className="ema-cat-pill">{t.category || '—'}</span></td>
                          <td>{fmtNum(t.campaigns_count)}</td>
                          <td>{fmtNum(t.total_sent)}</td>
                          <td>{fmtNum(t.total_delivered)}</td>
                          <td>{fmtPct(t.avg_open_rate || t.open_rate)}</td>
                          <td>{fmtPct(t.avg_click_rate || t.click_rate)}</td>
                          <td className={Number(t.total_failed || 0) > 0 ? 'ema-text-danger' : ''}>{fmtNum(t.total_failed)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailAnalytics;
